import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { Card, StarRating } from 'components';
import { Book, Review } from 'types';
import styles from './book-of-the-day.module.scss';

type BookOfTheDayProps = {
  bookOfTheDay: Book;
  reviewsOfTheDay: Review[];
  onBookSelect: (book: Book, reviewId?: number) => void;
  onBookmark: (id: number) => void;
};

const useFitLines = <T extends HTMLElement>(text: string) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    const container = el?.parentElement;

    if (!el || !container || typeof ResizeObserver === 'undefined') {
      return;
    }

    const fit = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);

      if (!lineHeight) {
        return;
      }

      const lines = Math.max(
        1,
        Math.floor(container.clientHeight / lineHeight),
      );
      el.style.setProperty('--description-lines', String(lines));
    };

    const observer = new ResizeObserver(fit);
    observer.observe(container);
    fit();

    return () => observer.disconnect();
  }, [text]);

  return ref;
};

export const BookOfTheDay = ({
  bookOfTheDay,
  reviewsOfTheDay,
  onBookSelect,
  onBookmark,
}: BookOfTheDayProps) => {
  const descriptionRef = useFitLines<HTMLParagraphElement>(
    bookOfTheDay.description,
  );
  const hasReviews = reviewsOfTheDay.length > 0;
  const stats = [
    { label: 'Pages', value: bookOfTheDay.pages },
    { label: 'Published', value: bookOfTheDay.published },
    { label: 'Language', value: bookOfTheDay.language },
  ].filter(({ value }) => value);

  return (
    <div className={styles.wrapper}>
      <h2>Book of the Day</h2>
      <div
        className={cn(styles.content, { [styles.noReviews]: !hasReviews })}
      >
        <Card
          book={bookOfTheDay}
          onSelect={onBookSelect}
          onBookmark={onBookmark}
        />
        {hasReviews && (
          <div className={styles.reviewsWrap}>
            {reviewsOfTheDay.map((review) => (
              <ReviewOfTheDay
                key={review.id}
                review={review}
                onRead={() => onBookSelect(bookOfTheDay, review.id)}
              />
            ))}
          </div>
        )}
        <aside className={styles.infoPanel}>
          {bookOfTheDay.description && (
            <div className={styles.infoDescriptionWrap}>
              <p ref={descriptionRef} className={styles.infoDescription}>
                {bookOfTheDay.description}
              </p>
            </div>
          )}
          {stats.length > 0 && (
            <>
              <hr className={styles.infoDivider} />
              <div className={styles.infoStats}>
                {stats.map(({ label, value }) => (
                  <div key={label} className={styles.infoStat}>
                    <span className={styles.infoStatLabel}>{label}</span>
                    <span className={styles.infoStatValue}>{value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <hr className={styles.infoDivider} />
          <div className={styles.infoTags}>
            {bookOfTheDay.categories.map((cat) => (
              <span key={cat} className={styles.infoTag}>
                {cat}
              </span>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

type ReviewOfTheDayProps = {
  review: Review;
  onRead: () => void;
};

const ReviewOfTheDay = ({ review, onRead }: ReviewOfTheDayProps) => {
  const textRef = useFitLines<HTMLParagraphElement>(review.text);

  return (
    <section className={styles.reviewSection}>
      <div className={styles.reviewHeader}>
        <div className={styles.avatar}>{review.authorInitials}</div>
        <div>
          <h3 className={styles.reviewTitle}>
            Review from {review.authorName}
          </h3>
          <div className={styles.reviewMeta}>
            <StarRating rating={review.rating} outOf={10} size={13} />
            <span>{review.rating}/10</span>
          </div>
        </div>
      </div>
      <div className={styles.reviewTextWrap}>
        <p ref={textRef} className={styles.reviewText}>
          {review.text}
        </p>
      </div>
      <div className={styles.reviewFooter}>
        <button className={styles.readMore} onClick={onRead}>
          Read the review →
        </button>
      </div>
    </section>
  );
};
